import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography } from "@mui/material";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { generateLoginMetrics } from '../../utilityFunctions/DataloveUtil';
import Swal from 'sweetalert';
import CustomLoader from "../../components/CustomLoader";

export default function HeatmapChart(props) {
    const { loginUser } = props;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchLoginMetrics = async () => {
            setLoading(true);
            const loginMetrics = await generateLoginMetrics(loginUser.jwtToken);
            setLoading(false);

            if (!loginMetrics) {
                Swal({
                    title: "Error",
                    text: "Failed to fetch login metrics",
                    icon: "error",
                    button: "Ok",
                });
                return;
            }

            setData(loginMetrics.heatmapData || []);
        };
        fetchLoginMetrics();
    }, [loginUser.jwtToken]);

    // Function to determine color based on count value
    const getColor = (count) => {
        if (count <= 5) return '#e6f7ff';
        if (count <= 10) return '#91d5ff';
        if (count <= 20) return '#69c0ff';
        if (count <= 30) return '#40a9ff';
        if (count <= 40) return '#1890ff';
        if (count <= 50) return '#096dd9';
        return '#0050b3';
    };

    // Custom tooltip to display data
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
                    <p>{`Day: ${data.day}`}</p>
                    <p>{`Hour: ${data.hour}:00`}</p>
                    <p>{`Count: ${data.count}`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: {xs: "start", sm: "start", md: "center"}, width: "95%", height: "90%", textAlign: "center", marginTop: { xs: "4.5rem", sm: "2rem", md: "0" } }}>
                {loading ? (
                    <CustomLoader />
                ) : (
                    <Grid container direction="row" spacing={1} sx={{display: "flex", justifyContent: "center"}}>
                        <Grid item xs={12}>
                            <Typography variant="h4" sx={{ marginTop: "2rem", fontWeight: "bold" }}>Login Activity Heatmap</Typography>
                        </Grid>
                        {data && data.length > 0 ? (
                            <Grid item xs={12} sx={{width: "100%", height: 500}}>
                                <ResponsiveContainer width="100%" height={500}>
                                    <ScatterChart
                                        margin={{
                                            top: 20,
                                            right: 20,
                                            bottom: 20,
                                            left: 20,
                                        }}
                                    >
                                        <XAxis 
                                            type="category" 
                                            dataKey="day" 
                                            name="Day" 
                                            allowDuplicatedCategory={false}
                                            tick={{ fontSize: 12 }}
                                        />
                                        <YAxis 
                                            type="number" 
                                            dataKey="hour" 
                                            name="Hour" 
                                            domain={[0, 23]}
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(value) => `${value}:00`}
                                        />
                                        <ZAxis 
                                            type="number" 
                                            dataKey="count" 
                                            range={[100, 500]} 
                                            name="Count" 
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Scatter name="Login Activity" data={data}>
                                            {data.map((entry, index) => (
                                                <Cell 
                                                    key={`cell-${index}`} 
                                                    fill={getColor(entry.count)} 
                                                />
                                            ))}
                                        </Scatter>
                                    </ScatterChart>
                                </ResponsiveContainer>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Typography variant="body1">
                                    No data available.
                                </Typography>                                
                            </Grid>
                        )}
                    </Grid>
                )}           
            </Box>
        </Box>
    );
} 